import { rosterWindowSettingsContract } from "@clipboard-health/contract-backend-main";
import { patch } from "@src/appV2/api/parsedApi";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { type RosterWindowRules, type RosterWindowSettings } from "../types";
import { invalidateRosterWindowSettingsQueries } from "./invalidateRosterWindowSettingsQueries";
import { rosterWindowSettingsUrl } from "./rosterWindowSettingsUrl";

interface UpdateRosterWindowSettingsParams {
  workplaceId: string;
  attributes: RosterWindowRules;
}

const PRN_RESOURCE_TYPE = "roster-window-setting";

export function useUpdateRosterWindowSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateRosterWindowSettingsParams): Promise<RosterWindowSettings> => {
      const response = await patch({
        url: rosterWindowSettingsUrl(params.workplaceId),
        data: { data: { type: PRN_RESOURCE_TYPE, attributes: params.attributes } },
        bodySchema: rosterWindowSettingsContract.updateSettings.body,
        responseSchema: rosterWindowSettingsContract.updateSettings.responses[200],
      });
      const envelope = response.data;
      return {
        workplaceId: envelope.data.id,
        longLeadThresholdDays: envelope.data.attributes.longLeadThresholdDays,
        longLeadOpenDaysBeforeStart: envelope.data.attributes.longLeadOpenDaysBeforeStart,
        shortLeadOpenDaysAfterPosting: envelope.data.attributes.shortLeadOpenDaysAfterPosting,
        urgentThresholdDays: envelope.data.attributes.urgentThresholdDays,
        updatedAt: envelope.data.attributes.updatedAt,
      };
    },
    onSuccess: async () => {
      await invalidateRosterWindowSettingsQueries(queryClient);
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.UPDATE_ROSTER_WINDOW_SETTINGS_FAILURE,
    },
  });
}
